import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Lenis from "lenis";
import {
  Menu,
  X,
  Youtube,
  Music2,
  ArrowUpRight,
  Mail,
  MapPin,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "/hero-desktop.jpg";
const HERO_MOBILE_IMG = "/hero-mobile.jpg";
const LOGO_IMG = "/logo.png";
const _UNUSED_HERO =
  "https://images.unsplash.com/photo-1775892976875-c0d70cfac456?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxtb29keSUyMG5vcndheSUyMGZqb3JkJTIwbWlzdHxlbnwwfHx8fDE3NzgxMDE1NTN8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_alternative-dredge/artifacts/et5l5ub8_The-Dredge_Press_Photo_2.jpg";
const TOUR_BG =
  "https://customer-assets.emergentagent.com/job_alternative-dredge/artifacts/qcmqqy2q_2023-07-12%2021.16.17.jpg";
const PIANO_IMG =
  "https://i.scdn.co/image/ab67616d0000b273b5948738f3d3147a877c546a";

const ALBUM_COVER = "/album-torches.jpg";
const ALBUM_DTB_COVER = "/album-down-the-beach.jpg";

// Torches (2024) — full tracklist from Spotify album 7Bg4XhBa57KVzGnFvZ80sJ
const TRACKS = [
  { n: "01", title: "Boredom",                            length: "2:49", url: "https://open.spotify.com/track/0OAlXf41MKi9hYVLxxvvz8" },
  { n: "02", title: "Whales",                             length: "4:04", url: "https://open.spotify.com/track/09OUL0MeBmDqlRB5BnZlyM" },
  { n: "03", title: "Torches",                            length: "4:37", url: "https://open.spotify.com/track/3B8ndtPoxT411BAzCxPKze" },
  { n: "04", title: "Brake",                              length: "4:11", url: "https://open.spotify.com/track/4KJNWx7KXMQ177qK6CDMe7" },
  { n: "05", title: "River Twice",                        length: "2:32", url: "https://open.spotify.com/track/4VswFGbCsCF0jd9MWgAIqU" },
  { n: "06", title: "The Oil Has Reached The Delta",      length: "3:21", url: "https://open.spotify.com/track/3hygJm4X57Wr8IPnC0zICX" },
  { n: "07", title: "Hotel Drunk",                        length: "5:05", url: "https://open.spotify.com/track/7BvPw4g5V3Ico8tJwsCa7V" },
  { n: "08", title: "Torches II — The Future Is Burning", length: "4:04", url: "https://open.spotify.com/track/63HIQwKnsfQnZwbpxQgZhN" },
];

const SOCIALS = {
  website:     "https://thedredgemusic.com",
  instagram:   "https://instagram.com/thedredgmusic",
  facebook:    "https://www.facebook.com/thedredgemusic/",
  spotify:     "https://open.spotify.com/artist/1W83ezvg1ZPMfURvbJ54gD",
  bandcamp:    "https://thedredge1.bandcamp.com",
  bandsintown: "https://www.bandsintown.com/a/15535726-the-dredge",
  apple:       "https://music.apple.com/us/artist/the-dredge/1717799728",
  amazon:      "https://music.amazon.com/artists/B0CNTPQ3T3/the-dredge",
  deezer:      "https://www.deezer.com/en/artist/244326732",
  qobuz:       "https://www.qobuz.com/no-en/interpreter/the-dredge/20443266",
};

const REVIEWS_DTB = [
  { quote: "In short, you’ll realise that you never get bored with THE DREDGE. Their references to a variety of styles are more of a compliment than a sign of a lack of direction. In fact, after just one listen, you realise that they’re unique and endearing. Not only should you add this album to your collection, but you should also pick up ‘Torches’, their 2024 album. Long live diversity!", author: "Denis Boisvert", pub: "Profilprog", rating: "8.8 / 10" },
  { quote: "From the very first seconds of ‘Blink’, the listener is plunged into a unique world where conventions seem to have been left by the wayside. Whilst many bands seek to fit into a tradition or replicate a tried-and-tested formula, The Dredge prefers to follow its instincts and give free rein to its creativity. This approach gives rise to a deeply organic album, capable of shifting from almost pop-like atmospheres to more jazzy passages, without ever losing its coherence. The compositions flow one after another like a series of colourful paintings, forming a rich and captivating musical mosaic.", author: "Mazik", pub: "Mazik.info" },
  { quote: "Following a very promising debut album, the band from Bergen, Norway, has moved away from the raw rock of their early days toward a more complex and eclectic approach that could almost be described as fusion. The compositions continue to benefit from Kjetil Vikene’s detached yet deep and charismatic vocals, which are now enriched by a jazz-influenced instrumental style. The saxophone parts, in particular, bring a melancholic groove to the compositions, which remain somewhat offbeat and rarely immediately accessible. We particularly appreciate the lush ‘Nature’ and the impressive ‘Event,’ built on a series of very successful crescendos.", author: "Qunitin", pub: "Album Rock" },
  { quote: "To my ears, this album is much more immediately appealing, full of energy, clever riffs, and unexpected rhythms, where Torches was more moody and subtle. Throughout, Vikene’s strong vocals are front and center, and he’s up to the challenge of being the focus. Saxophone maestro Kjetil Møster gets in a few notable appearances as well. The Dredge doesn’t fit within the progressive rock genre particularly well, though the odd meters make it at least prog-adjacent, and the imaginative arrangements fit comfortably with other musically sophisticated bands prog and otherwise. I might summarize my initial reaction to The Dredge’s debut as, ‘Well, that was pretty good,’ but from the first listen of Down the Beach, it was, ‘Oh, yes, I need to listen to this again!’", author: "Jon Davies", pub: "Expose" },
  { quote: "‘Animal’ […] demonstrates even more clearly just how keen the Norwegians are to experiment: whilst it starts off with a thoroughly pleasing rock sound, with a slight nod to THE STROKES, we suddenly find ourselves in the middle section with a thoroughly wild saxophone solo that definitively highlights their love of jazz and very deliberately breaks with familiar listening conventions. ‘Nature’ then goes one step further, presenting itself not only rhythmically as a laid-back bar-jazz number, but also taking it up a notch lyrically when Kjetil lists the sometimes bewildering contradictions that are not in his nature. […] ‘Down The Beach’ remains an interesting listen for anyone who enjoys alternative rock with an open mind, as this second album already hints at the potential that lies dormant within THE DREDGE.", author: "Tobias Dahs", pub: "Powermetal.de", rating: "7 / 10" },
  { quote: "[…] following the thread of their previous work while still making space to create something new. They recently released a full-length album titled Down the Beach via Apollon Records. Tracks like lead single ‘Animal’ hone in on their unique sound of post-rock-meets-trip-hop, with a clear current of jazz running through it. The Dredge have referred to their genre as ‘groovy melancholia,’ and we are inclined to agree after hearing Down the Beach in its entirety.", pub: "New Noise Magazine" },
  { quote: "The band draws inspiration from a wide range of genres, including touches of drum and bass and lounge jazz, a bit of art rock, and a few pinches of progressive rock. A patchwork that transports us to a variety of worlds, where the quality of the melodies is paramount. Without ever becoming overly complex, ‘Down The Beach’ glides seamlessly between rhythmic passages, acoustic sections and almost cinematic climaxes; this is its true masterstroke: it is both peaceful and urgent. […] Another triumph for The Dredge!", author: "Gabriel", pub: "ProgCritique", rating: "3 / 3" },
  { quote: "Boundaries, categorizations, and conventional thinking are luckily irrelevant to these chaps. […] musically speaking, it feels much more challenging and unpredictable in the positive sense of those words, and the creative evolution that is reflected throughout the album almost reads like a statement of intent at times. Their most radiant work yet.", author: "Jens Nepper", pub: "Eternal Terror", rating: "4.5 / 6" },
];

const REVIEWS = [
  { quote: "I can't remember hearing anything that sounds exactly like The Dredge; this is raw and groovy melancholy that makes me long for both desolate mountains and packed club shows all at once. More of this!", author: "Petter Lønningen", pub: "Bergens Tidende", rating: "5 / 6" },
  { quote: "Torches is undoubtedly an expertly written record that contains a multitude of interesting flavors, textures, and surprises. Very cool and most definitely worthwhile.", author: "Jens Nepper", pub: "Eternal Terror", rating: "4.5 / 6" },
  { quote: "The drama, intense moments and sophisticated songwriting are more than impressive. The guitars maltreat themselves from post-rock to fuzz and shoegaze — goosebump-inducing moments provide a pleasant tingling sensation.", author: "Carsten Agathe", pub: "Betreutes Proggen (DE)", rating: "12 / 15" },
  { quote: "The Dredge draw from a wealth of influences across these unpredictable and exciting 8 tracks — every moment here is precise, memorable and will be enjoyed on repeated visits.", author: null, pub: "Take Effect", rating: "8 / 10" },
  { quote: "This fusion of styles throughout the album works perfectly. The Dredge's Torches is a solid album, well worth the effort.", author: "Gabriel", pub: "Prog Critique (FR)", rating: "3 / 3" },
  { quote: "Just the way I like it! I pity the journalists that will have to 'explain' this to potential audiences — I look forward to seeing them squirm and fail, comparing it to any other band.", author: "Florian Magnus Mayer", pub: null, rating: null },
  { quote: "Torches excels both in electrifying ballads carried by Kjetil Vikene’s detached vocals and in particularly biting and catchy tracks. A debut album that marks a very promising start, much like the track ‘Whales,’ a true gem reminiscent of QOTSA’s best songs and one that proves deeply addictive to listen to.", author: "Qunitin", pub: "Album Rock" },
  { quote: "their debut Torches is a strong modern post-progressive rock adventure, with an alternative rock touch as well here and there. […] The sound is massive, and the performance is of a very high level, while the songs are quite strong and also very melodic.", pub: "Strutterzine", rating: "8.5 / 10" },

];

const MEMBERS = [
  { name: "Kjetil Vikene", role: "Vocals · Guitars · Bass · Keys · &c.", image: "/kjetil.jpg" },
  { name: "Frode Røsjø",   role: "Drums · Vocals · &c.",                  image: "/frode.jpg?v=2" },
];

const GALLERY = [
  { src: HERO_IMG,  cls: "g-wide2" },
  { src: ABOUT_IMG, cls: "g-sq2" },
];

const PHOTO_CREDIT = { name: "Thor Brødreskift", url: "https://thorbrodreskift.com/" };

const ABOUT_SLIDES = [
  "/about-1.jpg?v=2",
  "/about-2.jpg",
  "/about-3.jpg",
  "/about-4.jpg?v=2",
  "/about-5.jpg",
  "/about-6.jpg",
  "/about-7.jpg",
  "/about-8.jpg",
  "/about-9.jpg",
];

const HERO_MOBILE_V = "/hero-mobile.jpg?v=4";

const FadeSlideshow = ({ slides = [], interval = 4500 }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [slides.length, interval]);
  return (
    <div className="relative w-full h-full" data-testid="about-slideshow">
      {slides.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          loading={i === 0 ? "eager" : "lazy"}
          data-testid={`about-slide-${i}`}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: i === idx ? 1 : 0,
            transition: "opacity 1.6s ease-in-out",
            filter: "grayscale(0.15) contrast(1.05)",
          }}
        />
      ))}
    </div>
  );
};

// Reveal-on-scroll hook
function useReveal() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const Header = () => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const items = [
    { href: "#new-album", label: "New Music", testid: "nav-singles" },
    { href: "#about",     label: "About",     testid: "nav-about" },
    { href: "#music",     label: "Old Music", testid: "nav-music" },
    { href: "#press",     label: "Press",     testid: "nav-press" },
    { href: "#shows",     label: "Live",      testid: "nav-shows" },
    { href: "#contact",   label: "Contact",   testid: "nav-contact" },
  ];
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-40"
      style={{
        backdropFilter: "blur(18px)",
        background: "rgba(255,255,255,0.78)",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <div className="flex items-center justify-between px-6 md:px-10 py-5">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-3" onClick={close}>
          <img src={LOGO_IMG} alt="The Dredge" className="h-7 md:h-8 w-auto" />
        </a>
        <nav className="hidden md:flex gap-8 lg:gap-10 overline">
          {items.map((it) => (
            <a key={it.href} href={it.href} data-testid={it.testid} className="link-underline">{it.label}</a>
          ))}
        </nav>
        <button
          type="button"
          data-testid="nav-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((o) => !o)}
          className="md:hidden flex items-center justify-center"
          style={{ width: 36, height: 36, color: "var(--text-0)" }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile dropdown */}
      <div
        data-testid="nav-mobile"
        className="md:hidden overflow-hidden"
        style={{
          maxHeight: open ? 480 : 0,
          transition: "max-height 0.45s cubic-bezier(0.2, 0.7, 0.2, 1)",
          borderTop: open ? "1px solid var(--border-subtle)" : "1px solid transparent",
          background: "rgba(255,255,255,0.96)",
        }}
      >
        <nav className="flex flex-col px-6 py-4 gap-1">
          {items.map((it) => (
            <a
              key={it.href}
              href={it.href}
              data-testid={`${it.testid}-mobile`}
              onClick={close}
              className="overline py-3 link-underline"
              style={{ color: "var(--text-0)", borderBottom: "1px solid var(--border-subtle)" }}
            >
              {it.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
};

const AboutLead = () => (
  <div id="about" data-testid="about-lead" className="px-6 md:px-12 pt-24 md:pt-32 pb-6 md:pb-8" style={{ background: "var(--bg-0)" }}>
    <div className="max-w-7xl mx-auto">
      <div className="overline reveal">02 / The Band</div>
    </div>
  </div>
);

const Hero = () => (
  <section id="top" data-testid="hero-section" className="hero-wrap">
    <picture className="hero-bg" data-testid="hero-picture">
      <source media="(max-width: 768px)" srcSet={HERO_MOBILE_V} />
      <img src={HERO_IMG} alt="" />
    </picture>
    <div className="absolute inset-0 flex flex-col justify-between px-6 md:px-12 pt-6 md:pt-10 pb-24 md:pb-24">
      <div className="flex justify-between items-start overline" style={{ color: "var(--text-1)" }}>
        <div>
          <div>— Bergen, Norway · 60.39°N 5.32°E</div>
        </div>
      </div>

      <div className="relative">
        <h1
          data-testid="hero-title"
          className="dredge-wordmark"
          style={{
            fontSize: "clamp(72px, 18vw, 280px)",
            color: "var(--text-0)",
          }}
        >
          The Dredge
        </h1>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-6">
          <p className="max-w-md font-mono-dredge text-sm leading-relaxed" style={{ color: "var(--text-1)" }}>
          Kjetil Vikene &amp; Frode Røsjø — an alt-pop / post-rock duo with a
          jazzy streak. <em className="font-emph">Groovy
          melancholia</em>. Down the Beach out 05. June.
        </p>
          {/* Hidden for now — to be re-enabled when the band wants prominent CTAs back.
          <div className="flex gap-4">
            <a href="#music"  data-testid="hero-listen-btn"  className="btn-ghost">Listen <ArrowUpRight size={14} /></a>
            <a href="#shows"  data-testid="hero-shows-btn"   className="btn-ghost" style={{ borderColor: "var(--border-strong)", color: "var(--text-0)" }}>Shows</a>
          </div>
          */}
        </div>
      </div>
    </div>
  </section>
);

const QUOTE_SLIDES = [
  { q: "In fact, after just one listen, you realise that they’re unique and endearing. Not only should you add this album to your collection, but you should also pick up ‘Torches’, their 2024 album. Long live diversity!", by: "Profilprog" },
  { q: "The Dredge prefers to follow its instincts and give free rein to its creativity. The compositions flow one after another like a series of colourful paintings, forming a rich and captivating musical mosaic.", by: "Mazik.info" },
  { q: "The compositions continue to benefit from Kjetil Vikene’s detached yet deep and charismatic vocals, which are now enriched by a jazz-influenced instrumental style.", by: "Album Rock" },
  { q: "‘Down The Beach’ remains an interesting listen for anyone who enjoys alternative rock with an open mind, as this second album already hints at the potential that lies dormant within The Dredge.", by: "Powermetal.de" },
  { q: "Tracks like lead single ‘Animal’ hone in on their unique sound of post-rock-meets-trip-hop, with a clear current of jazz running through it.", by: "New Noise Magazine" },
  { q: "Without ever becoming overly complex, ‘Down The Beach’ glides seamlessly between rhythmic passages, acoustic sections and almost cinematic climaxes; this is its true masterstroke. Another triumph for The Dredge!", by: "ProgCritique" },
  { q: "Boundaries, categorizations, and conventional thinking are luckily irrelevant to these chaps. […] Their most radiant work yet.", by: "Eternal Terror" },
];

const QuoteSlideshow = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % QUOTE_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="quote-slideshow" className="grid mb-10" aria-live="polite">
      {QUOTE_SLIDES.map((s, i) => (
        <figure
          key={i}
          data-testid={`quote-slide-${i}`}
          aria-hidden={i !== idx}
          style={{ gridArea: "1 / 1", opacity: i === idx ? 1 : 0, transform: i === idx ? "translateY(0)" : "translateY(8px)", transition: "opacity 900ms ease, transform 900ms ease" }}
        >
          <blockquote className="font-serif-dredge italic text-lg md:text-xl leading-snug" style={{ color: "var(--text-0)" }}>“{s.q}”</blockquote>
          <figcaption className="overline mt-4" style={{ color: "var(--rust)" }}>— {s.by}</figcaption>
        </figure>
      ))}
    </div>
  );
};

const NewAlbum = () => (
  <section id="new-album" data-testid="new-album-section" className="section-pad" style={{ background: "var(--bg-1)" }}>
    <div className="max-w-7xl mx-auto">
      <div className="overline reveal">01 / Out now</div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-12 mt-10 items-center">
        <div className="md:col-span-7 reveal">
          <div className="relative overflow-hidden" style={{ background: "#000" }}>
            <img
              data-testid="new-album-cover"
              src={ALBUM_DTB_COVER}
              alt="Down the Beach — album cover"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
        </div>
        <div className="md:col-span-5 reveal">
          <QuoteSlideshow />
          <h2
            className="font-serif-dredge font-light leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(44px, 7vw, 96px)" }}
          >
            New album <span className="italic" style={{ color: "var(--rust)" }}>out now!</span>
          </h2>
          <div className="hair-line my-8" />
          <div className="overline" style={{ color: "var(--text-1)" }}>
            <span className="font-emph" style={{ color: "var(--rust)" }}>Down the Beach</span> · LP · Apollon Records
          </div>
          <div className="overline mt-3" style={{ color: "var(--text-2)" }}>
            The follow-up to <span className="font-emph">Torches</span> — alt-pop / post-rock with a jazzy streak
          </div>
        </div>
      </div>
      <div className="mt-12 md:mt-16 reveal">
        <iframe
          data-testid="new-album-spotify-embed"
          title="The Dredge — Down the Beach on Spotify"
          src="https://open.spotify.com/embed/album/35Pq0eHOF7bsKeowyhkala?utm_source=generator"
          width="100%"
          height="380"
          frameBorder="0"
          allowFullScreen
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          style={{ border: 0, borderRadius: 12, display: "block" }}
        />
      </div>
    </div>
  </section>
);

const About = () => (
  <section data-testid="about-section" className="section-pad" style={{ background: "var(--bg-0)" }}>
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-y-6 md:gap-10">
        <div className="col-span-12 md:col-span-7 reveal">
          <h2 className="font-serif-dredge font-light leading-[0.95] tracking-tight" style={{ fontSize: "clamp(44px, 7vw, 96px)" }}>
            <span className="italic">Yes,</span> but also
            <span style={{ color: "var(--rust)" }}>…</span>
          </h2>
          <div className="hair-line my-10" />
          <p className="font-mono-dredge text-sm leading-relaxed max-w-xl" style={{ color: "var(--text-1)" }}>
            More than a decade ago, a common friend handed me a rare copy of an
            EP from a completely unknown Norwegian band. The music — the strange
            and lyrically creative stories, combined with the band's
            shapeshifting conglomerates of inspiration — felt like
            <span className="font-emph"> Norway's best kept secret</span>.
          </p>
          <p className="font-mono-dredge text-sm leading-relaxed max-w-xl mt-6" style={{ color: "var(--text-1)" }}>
            After a long hiatus, the project's protagonists returned in 2024
            under the new moniker The Dredge with the full-length{" "}
            <em className="font-emph">Torches</em> — picking up
            where their previous iteration left off, with renewed vigour. One
            reviewer named the debut{" "}
            <span style={{ color: "var(--rust)" }} className="font-emph">“groovy melancholia”</span>. His mind{" "}
            <em className="font-emph">“goes into overdrive as
            it starts searching for references”</em> and he{" "}
            <em className="font-emph">“can't remember hearing
            anything that sounds exactly like The Dredge.”</em>
          </p>
          <p className="font-mono-dredge text-sm leading-relaxed max-w-xl mt-6" style={{ color: "var(--text-1)" }}>
            Now, after 30 years of collaborating, the duo is expanding the
            battleground again with their upcoming{" "}
            <em className="font-emph">Down the Beach</em>. What
            was an alt-pop / post-rock monster is now joined at the hip by a
            jazzy beast, with a herd of alternative rhythmic equus dragging the
            machinery — art conceived with complete creative freedom, while
            still being grooooovy.
          </p>
          <div className="overline mt-10" style={{ color: "var(--text-2)" }}>
            — Morean · Composer, Performer, Fan · Schiedam, March 2026
          </div>
        </div>

        <div className="col-span-12 md:col-span-5 reveal">
          <div className="relative aspect-square overflow-hidden" style={{ background: "#000" }}>
            <FadeSlideshow slides={ABOUT_SLIDES} />
          </div>
        </div>

        <div className="col-span-12 mt-6 md:mt-16 reveal">
          <div className="overline mb-8">— The Dredge is</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {MEMBERS.map((m, i) => (
              <div
                key={m.name}
                data-testid={`member-${i}`}
                className="relative overflow-hidden group"
                style={{ aspectRatio: "3/4", background: "#0A0B0E" }}
              >
                <img
                  src={m.image}
                  alt={m.name}
                  loading="lazy"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center top",
                    filter: "grayscale(0.15) contrast(1.05)",
                    transition: "transform 1.2s ease",
                  }}
                  className="group-hover:scale-[1.04]"
                />
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(10,11,14,0) 40%, rgba(10,11,14,0.55) 70%, rgba(10,11,14,0.92) 100%)",
                  }}
                />
                <div className="absolute left-0 right-0 bottom-0 p-6 md:p-8">
                  <div
                    className="font-serif-dredge text-3xl md:text-5xl italic tracking-tight"
                    style={{ color: "#F3F4F6", letterSpacing: "-0.02em" }}
                  >
                    {m.name}
                  </div>
                  <div className="overline mt-3" style={{ color: "var(--rust)" }}>
                    {m.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Music = () => {
  return (
    <section id="music" data-testid="music-section" className="section-pad" style={{ background: "var(--bg-1)" }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 reveal">
          <div>
            <div className="overline">03 / The First Album</div>
            <h2 className="font-serif-dredge font-light italic leading-[0.95] mt-4" style={{ fontSize: "clamp(44px, 7vw, 96px)" }}>
              Torches
            </h2>
            <div className="overline mt-4" style={{ color: "var(--text-1)" }}>
              LP · 8 tracks · 30:46 · Apollon Records · 05.04.2024
            </div>
          </div>
          <a data-testid="preorder-link" href={SOCIALS.spotify} target="_blank" rel="noreferrer" className="btn-ghost">Follow on Spotify <ArrowUpRight size={14} /></a>
        </div>

        <div className="mt-14 md:mt-20 reveal">
          <iframe
            data-testid="torches-album-embed"
            title="The Dredge — Torches on Spotify"
            src="https://open.spotify.com/embed/album/7Bg4XhBa57KVzGnFvZ80sJ?utm_source=generator"
            width="100%"
            height="380"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            style={{ border: 0, borderRadius: 12, display: "block" }}
          />
          <div className="mt-10 flex flex-wrap gap-4">
            <a data-testid="listen-bandcamp" href={SOCIALS.bandcamp} target="_blank" rel="noreferrer" className="btn-ghost">Bandcamp <ArrowUpRight size={14}/></a>
            <a data-testid="listen-spotify"  href={SOCIALS.spotify}  target="_blank" rel="noreferrer" className="btn-ghost" style={{ borderColor: "var(--border-strong)", color: "var(--text-0)" }}>Spotify <ArrowUpRight size={14}/></a>
          </div>
        </div>
      </div>
    </section>
  );
};

const PressHeading = ({ children, testid }) => (
  <h2 data-testid={testid} className="font-serif-dredge font-light italic leading-[0.95] mt-4 whitespace-nowrap" style={{ fontSize: "clamp(17px, 3.3vw, 52px)" }}>
    {children}<span style={{ color: "var(--rust)" }}>.</span>
  </h2>
);

const Reviews = () => (
  <section id="press" data-testid="press-section" className="section-pad" style={{ background: "var(--bg-0)" }}>
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-10 items-end reveal">
        <div className="col-span-12 md:col-span-9">
          <div className="overline">04 / Press</div>
          <PressHeading testid="press-heading-dtb">What they said about Down The Beach (2026)</PressHeading>
        </div>
        <div className="col-span-12 md:col-span-3 flex md:justify-end">
          <img src={LOGO_IMG} alt="The Dredge logo" data-testid="dredge-logo" className="w-40 md:w-56" style={{ filter: "brightness(0.95) saturate(0.9)" }} />
        </div>
      </div>
      <ReviewGrid items={REVIEWS_DTB} prefix="review-dtb" />
      <div className="mt-24 md:mt-32 reveal">
        <PressHeading testid="press-heading-torches">What they said about Torches (2024)</PressHeading>
      </div>
      <ReviewGrid items={REVIEWS} prefix="review" />
    </div>
  </section>
);

const ReviewGrid = ({ items, prefix }) => (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14 mt-16">
        {items.map((r, i) => (
          <figure key={i} data-testid={`${prefix}-${i}`} className="reveal">
            <div className="overline" style={{ color: "var(--rust)" }}>{r.rating || "—"}</div>
            <blockquote className="font-serif-dredge text-2xl md:text-3xl italic leading-snug mt-4" style={{ letterSpacing: "-0.01em", color: "var(--text-0)" }}>
              “{r.quote}”
            </blockquote>
            <figcaption className="overline mt-6" style={{ color: "var(--text-2)" }}>
              {r.author ? `— ${r.author}` : "—"}{r.author && r.pub ? `, ${r.pub}` : (!r.author && r.pub ? ` ${r.pub}` : "")}
            </figcaption>
          </figure>
        ))}
      </div>
);


const Shows = () => {
  const [shows, setShows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/shows`)
      .then((r) => setShows(r.data || []))
      .catch(() => setErr("Unable to load shows right now."));
  }, []);

  return (
    <section id="shows" data-testid="shows-section" className="section-pad relative" style={{ background: "var(--bg-0)" }}>
      <div
        aria-hidden
        className="absolute inset-0 opacity-25"
        style={{ backgroundImage: `url(${TOUR_BG})`, backgroundSize: "cover", backgroundPosition: "center", filter: "grayscale(0.5)", maskImage: "linear-gradient(180deg, transparent, black 30%, black 70%, transparent)", WebkitMaskImage: "linear-gradient(180deg, transparent, black 30%, black 70%, transparent)" }}
      />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 reveal">
          <div>
            <div className="overline">05 / On the road</div>
            <h2 className="font-serif-dredge font-light leading-[0.95] mt-4" style={{ fontSize: "clamp(44px, 7vw, 96px)" }}>
              Live<span className="italic" style={{ color: "var(--rust)" }}>.</span>
            </h2>
          </div>
          <a
            data-testid="bandsintown-link"
            href="https://www.bandsintown.com/a/15535726-the-dredge"
            target="_blank"
            rel="noreferrer"
            className="overline inline-flex items-center gap-2"
            style={{ color: "var(--rust)" }}
          >
            Follow on Bandsintown <ArrowUpRight size={14} />
          </a>
        </div>

        <div className="mt-14">
          {err && <div data-testid="shows-error" className="overline" style={{ color: "var(--rust)" }}>{err}</div>}
          {shows.map((s, i) => {
            const d = new Date(s.date);
            const day = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).toUpperCase();
            const yr  = d.getFullYear();
            return (
              <div key={s.id} data-testid={`show-row-${i}`} className="tour-row">
                <div className="font-mono-dredge text-xs" style={{ color: "var(--text-1)" }}>
                  <div className="text-base" style={{ color: "inherit" }}>{day}</div>
                  <div className="mt-1" style={{ color: "var(--text-2)" }}>{yr}</div>
                </div>
                <div>
                  <div className="font-serif-dredge text-2xl md:text-3xl italic tracking-tight">{s.city}</div>
                  <div className="overline mt-1" style={{ color: "var(--text-2)" }}>{s.country}</div>
                </div>
                <div className="tour-venue font-mono-dredge text-sm" style={{ color: "var(--text-1)" }}>
                  <div>{s.venue}</div>
                </div>
                <div className="tour-cta">
                  {s.time && (
                    <span data-testid={`show-time-${i}`} className="overline" style={{ color: "var(--text-2)" }}>{s.time}</span>
                  )}
                </div>
              </div>
            );
          })}
          <div className="mt-12 overline" style={{ color: "var(--text-2)" }}>
            More dates being announced — follow on{" "}
            <a
              data-testid="bandsintown-link-inline"
              className="link-underline"
              href="https://www.bandsintown.com/a/15535726-the-dredge"
              target="_blank"
              rel="noreferrer"
              style={{ color: "var(--rust)" }}
            >
              Bandsintown
            </a>{" "}
            for alerts.
          </div>
        </div>
      </div>
    </section>
  );
};

const Gallery = () => (
  <section id="gallery" data-testid="gallery-section" className="section-pad" style={{ background: "var(--bg-1)" }}>
    <div className="max-w-7xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4 reveal">
        <div>
          <div className="overline">06 / Pics</div>
          <h2 className="font-serif-dredge font-light italic leading-[0.95] mt-4" style={{ fontSize: "clamp(44px, 7vw, 96px)" }}>
            Manning the ship.
          </h2>
          <div className="overline mt-3" style={{ color: "var(--text-2)" }}>
            Photos · <a href={PHOTO_CREDIT.url} target="_blank" rel="noreferrer" data-testid="pics-photo-credit" className="link-underline" style={{ color: "var(--rust)" }}>{PHOTO_CREDIT.name}</a>
          </div>
        </div>
      </div>
      <div className="gallery-grid mt-14 reveal">
        {GALLERY.map((g, i) => (
          <div key={i} className={`g-item ${g.cls}`} data-testid={`gallery-item-${i}`}>
            <img src={g.src} alt="" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Contact = () => {
  return (
    <section id="contact" data-testid="contact-section" className="section-pad" style={{ background: "var(--bg-0)" }}>
      <div className="max-w-7xl mx-auto">
        <div className="overline reveal">07 / Let's talk</div>
        <h2 className="font-serif-dredge font-light leading-[0.9] tracking-tighter mt-6 reveal"
            style={{ fontSize: "clamp(40px, 10vw, 140px)", overflowWrap: "anywhere" }}>
          Contact <span style={{ color: "var(--rust)" }}>/</span> Booking<span className="italic" style={{ color: "var(--rust)" }}>.</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-10 mt-20">
          {/* Intro + location + follow */}
          <div className="col-span-12 md:col-span-5 reveal">
            <p className="font-mono-dredge text-sm leading-relaxed" style={{ color: "var(--text-1)" }}>
              Booking enquiries, press, collaborations — write directly. Bergen-based, touring the Nordic circuit and beyond.
            </p>

            <div className="hair-line my-12" />

            <div className="space-y-3 font-mono-dredge text-sm" style={{ color: "var(--text-1)" }}>
              <div className="flex items-center gap-3"><MapPin size={14} /> Bergen, Norway</div>
            </div>

            <div className="hair-line my-12" />

            <div className="overline mb-4" style={{ color: "var(--text-2)" }}>Follow</div>
            <div className="flex flex-wrap gap-x-6 gap-y-3 overline">
              <a data-testid="contact-social-instagram" target="_blank" rel="noreferrer" href={SOCIALS.instagram} className="link-underline" style={{ color: "var(--rust)" }}>Instagram ↗</a>
              <a data-testid="contact-social-facebook"  target="_blank" rel="noreferrer" href={SOCIALS.facebook}  className="link-underline" style={{ color: "var(--rust)" }}>Facebook ↗</a>
              <a data-testid="contact-social-spotify"   target="_blank" rel="noreferrer" href={SOCIALS.spotify}   className="link-underline" style={{ color: "var(--rust)" }}>Spotify ↗</a>
            </div>
          </div>

          {/* Booking · Press · Collab + emails */}
          <div className="col-span-12 md:col-span-7 reveal">
            <div className="overline mb-10">Booking · Press · Collab</div>

            <div className="space-y-10 font-mono-dredge text-sm" style={{ color: "var(--text-1)" }}>
              <div className="flex items-start gap-4">
                <Mail size={16} className="mt-1 shrink-0" style={{ color: "var(--rust)" }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <a data-testid="email-band" className="font-serif-dredge italic text-xl md:text-3xl link-underline break-all" href="mailto:TheDredgeMusic@gmail.com" style={{ letterSpacing: "-0.01em", color: "var(--text-0)", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    TheDredgeMusic@gmail.com
                  </a>
                  <div className="overline mt-2" style={{ color: "var(--text-2)" }}>Band — general &amp; press</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={16} className="mt-1 shrink-0" style={{ color: "var(--rust)" }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <a data-testid="email-label" className="font-serif-dredge italic text-xl md:text-3xl link-underline break-all" href="mailto:robin@karismarecords.no" style={{ letterSpacing: "-0.01em", color: "var(--text-0)", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    robin@karismarecords.no
                  </a>
                  <div className="overline mt-2" style={{ color: "var(--text-2)" }}>Label — Apollon Records · Robin Mortensen</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="px-6 md:px-12 py-14 border-t" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-0)" }}>
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
        <div className="flex items-center gap-4">
          <img src={LOGO_IMG} alt="The Dredge" className="h-12 w-auto" />
          <div>
            <div className="font-serif-dredge italic text-2xl">The Dredge</div>
            <a data-testid="footer-website" target="_blank" rel="noreferrer" href={SOCIALS.website} className="overline link-underline" style={{ color: "var(--rust)" }}>thedredgemusic.com ↗</a>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-10 gap-y-3 overline" style={{ color: "var(--text-1)" }}>
          <a data-testid="social-spotify"     target="_blank" rel="noreferrer" href={SOCIALS.spotify}     className="link-underline">Spotify ↗</a>
          <a data-testid="social-bandcamp"    target="_blank" rel="noreferrer" href={SOCIALS.bandcamp}    className="link-underline">Bandcamp ↗</a>
          <a data-testid="social-bandsintown" target="_blank" rel="noreferrer" href={SOCIALS.bandsintown} className="link-underline">Bandsintown ↗</a>
          <a data-testid="social-instagram"   target="_blank" rel="noreferrer" href={SOCIALS.instagram}   className="link-underline">Instagram ↗</a>
          <a data-testid="social-facebook"    target="_blank" rel="noreferrer" href={SOCIALS.facebook}    className="link-underline">Facebook ↗</a>
          <a data-testid="social-apple"       target="_blank" rel="noreferrer" href={SOCIALS.apple}       className="link-underline">Apple Music ↗</a>
          <a data-testid="social-amazon"      target="_blank" rel="noreferrer" href={SOCIALS.amazon}      className="link-underline">Amazon Music ↗</a>
          <a data-testid="social-deezer"      target="_blank" rel="noreferrer" href={SOCIALS.deezer}      className="link-underline">Deezer ↗</a>
          <a data-testid="social-qobuz"       target="_blank" rel="noreferrer" href={SOCIALS.qobuz}       className="link-underline">Qobuz ↗</a>
        </div>
      </div>
      <div className="overline mt-10" style={{ color: "var(--text-2)" }}>© {new Date().getFullYear()} The Dredge · Bergen, Norway</div>
    </div>
  </footer>
);

function App() {
  useReveal();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.09 });
    lenisRef.current = lenis;
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  // ping API to verify connectivity (non-blocking)
  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App">
      <Header />
      <NewAlbum />
      <AboutLead />
      <Hero />
      <About />
      <Music />
      <Reviews />
      <Shows />
      <Gallery />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
